import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Upload, 
  FileText, 
  MessageSquare, 
  Trash2,
  File,
  FileSpreadsheet,
  FileType,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowLeft,
  Building,
  Search,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  'Logistics': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Safety': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Financial': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Operations': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'HR': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  'Admin': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Project Management': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'IT': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  'Business Development': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'Engineering': 'bg-teal-500/20 text-teal-400 border-teal-500/30'
};

const getFileIcon = (contentType, filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return <FileText className="w-5 h-5 text-red-400" />;
  if (['doc', 'docx'].includes(ext)) return <FileType className="w-5 h-5 text-blue-400" />;
  if (['xls', 'xlsx'].includes(ext)) return <FileSpreadsheet className="w-5 h-5 text-green-400" />;
  return <File className="w-5 h-5 text-muted-foreground" />;
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'indexed':
      return (
        <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full">
          <CheckCircle className="w-3 h-3" />
          Indexed
        </span>
      );
    case 'indexing':
    case 'parsing':
      return (
        <span className="flex items-center gap-1 text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded-full">
          <Loader2 className="w-3 h-3 animate-spin" />
          {status === 'parsing' ? 'Parsing...' : 'Indexing...'}
        </span>
      );
    case 'uploaded':
      return (
        <span className="flex items-center gap-1 text-xs text-blue-400 bg-blue-500/10 px-2 py-1 rounded-full">
          <Loader2 className="w-3 h-3 animate-spin" />
          Processing...
        </span>
      );
    case 'failed':
      return (
        <span className="flex items-center gap-1 text-xs text-red-400 bg-red-500/10 px-2 py-1 rounded-full">
          <AlertCircle className="w-3 h-3" />
          Failed
        </span>
      );
    default:
      return null;
  }
};

export default function DepartmentDetailPage() {
  const { departmentId } = useParams();
  const navigate = useNavigate();
  const [department, setDepartment] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDepartmentData();
  }, [departmentId]);

  useEffect(() => {
    // Poll for document status updates
    const interval = setInterval(() => {
      if (documents.some(d => ['uploaded', 'parsing', 'indexing'].includes(d.status))) {
        fetchDocuments();
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [documents]);

  const fetchDepartmentData = async () => {
    try {
      const [deptRes, docsRes] = await Promise.all([
        axios.get(`${API}/departments/${departmentId}`),
        axios.get(`${API}/documents?department_id=${departmentId}`)
      ]);
      setDepartment(deptRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      console.error('Failed to fetch department:', error);
      toast.error('Failed to load department');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents?department_id=${departmentId}`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;

    setUploading(true);
    setUploadProgress(0);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('department_id', departmentId);

      try {
        const response = await axios.post(`${API}/documents/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              ((i + progressEvent.loaded / progressEvent.total) / files.length) * 100
            );
            setUploadProgress(progress);
          }
        });

        setDocuments(prev => [response.data, ...prev]);
        toast.success(`Uploaded ${file.name}`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || 'Unknown error'}`);
      }
    }

    setUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteDocument = async (docId, filename) => {
    if (!window.confirm(`Delete "${filename}"? This cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/documents/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!department) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">Department not found</p>
        <Link to="/dashboard/departments">
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Departments
          </Button>
        </Link>
      </div>
    );
  }

  const indexedCount = documents.filter(d => d.status === 'indexed').length;
  
  // Filter documents by search query
  const filteredDocuments = documents.filter(doc => 
    doc.filename?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Back Button - Prominent */}
      <Button 
        variant="outline" 
        onClick={() => navigate('/dashboard/departments')}
        className="flex items-center gap-2"
        data-testid="back-to-departments-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Departments
      </Button>
      
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground">{department.name}</h1>
            </div>
            <div className="flex items-center gap-3">
              <Badge 
                variant="outline" 
                className={CATEGORY_COLORS[department.category] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}
              >
                {department.category || 'Uncategorized'}
              </Badge>
              {department.description && (
                <p className="text-muted-foreground">{department.description}</p>
              )}
            </div>
          </div>
          
          <div className="flex gap-3">
            <Link to={`/dashboard/departments/${departmentId}/chat`}>
              <Button 
                className="btn-glow" 
                disabled={indexedCount === 0}
                data-testid="chat-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Chat with Docs
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{documents.length}</p>
                <p className="text-sm text-muted-foreground">Total Documents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{indexedCount}</p>
                <p className="text-sm text-muted-foreground">Indexed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Upload className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {formatBytes(documents.reduce((sum, d) => sum + (d.size_bytes || 0), 0))}
                </p>
                <p className="text-sm text-muted-foreground">Total Size</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Upload Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <div 
            className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              multiple
              accept=".pdf,.docx,.doc,.xlsx,.xls,.txt"
              className="hidden"
              data-testid="file-input"
            />
            <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-foreground font-medium mb-1">
              {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
            </p>
            <p className="text-sm text-muted-foreground">
              PDF, Word, Excel, or Text files
            </p>
            {uploading && (
              <div className="mt-4 max-w-xs mx-auto">
                <Progress value={uploadProgress} className="h-2" />
                <p className="text-xs text-muted-foreground mt-1">{uploadProgress}%</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg">Documents</CardTitle>
          {documents.length > 0 && (
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9 h-9"
                data-testid="search-documents-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No documents uploaded yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Upload documents to start asking questions
              </p>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No documents match "{searchQuery}"</p>
              <button 
                onClick={() => setSearchQuery('')}
                className="text-sm text-primary hover:underline mt-2"
              >
                Clear search
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {searchQuery && (
                <p className="text-sm text-muted-foreground mb-3">
                  Showing {filteredDocuments.length} of {documents.length} documents
                </p>
              )}
              {filteredDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors"
                  data-testid={`doc-${doc.id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {getFileIcon(doc.content_type, doc.filename)}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground truncate">{doc.filename}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{formatBytes(doc.size_bytes)}</span>
                        {doc.chunk_count > 0 && <span>{doc.chunk_count} chunks</span>}
                        <span>{formatDate(doc.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    {getStatusBadge(doc.status)}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteDocument(doc.id, doc.filename)}
                      className="text-muted-foreground hover:text-red-400"
                      data-testid={`delete-doc-${doc.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
